/**
 * Settings → Skills & Levels (SKILL-STEER-01, Seth 2026-09-23: "how does
 * pegasus know what types need more experienced techs and how do we maintain
 * that and more importantly manage that going forward as new equipment and
 * skills and growth happens without overcomplicating").
 *
 * ONE page holds every rule the booking grid uses to steer a call:
 *   · each tech's LEVEL (names editable — Maintenance / Service / Senior by
 *     default) and SPECIALTIES (a who-can-do-what matrix with coverage, so a
 *     specialty only one tech holds is visible before it bites),
 *   · each CALL TYPE's lowest level, specialties, and techs to offer first,
 *   · each kind of EQUIPMENT's specialty and techs to offer first ("rooftop
 *     units → Camron first").
 * Changes save as you make them and apply to the next booking. Nothing here
 * blocks a dispatcher — the grid offers the techs who fit; anyone can still
 * be picked, with a flag. Competitor frame: ServiceTitan keeps skills on
 * technicians + job types and ranks qualified techs first with an alert, not
 * a wall; Jobber/Housecall have no skill steering at all.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Boxes, Check, GraduationCap, Loader2, Pencil, Plus, RotateCcw, Tag, Users, X, type LucideIcon } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { useActiveTechs } from "@/features/board/api";
import { useServiceTypes, categoryLabel, type ServiceType } from "@/features/customer-360/serviceTypes";
import { useEquipmentCategories, useUpdateEquipmentCategory } from "./equipmentCategories";
import { useSaveOrgSetting } from "@/lib/orgSettings";
import {
  LEVELS,
  levelNeedText,
  useLevelLabels,
  useSaveSpecialty,
  useSaveTypeNeeds,
  useSetTechLevel,
  useSetTechSkill,
  useSpecialties,
  useTechQuals,
} from "@/features/team/skillSteer";

const errText = (e: unknown) => (e instanceof Error ? e.message : typeof e === "object" && e && "message" in e ? String((e as { message: unknown }).message) : String(e));

function SectionCard({ icon: Icon, title, info, right, children }: { icon: LucideIcon; title: string; info?: ReactNode; right?: ReactNode; children: ReactNode }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <Icon className="h-3.5 w-3.5" />
          </span>
          <span className="text-[13px] font-bold text-slate-800">{title}</span>
          {info && <InfoTip label={`About ${title.toLowerCase()}`}>{info}</InfoTip>}
        </div>
        {right}
      </div>
      <div className="px-4 py-3">{children}</div>
    </Card>
  );
}

/** Chips with × plus a "+ Add" picker — specialties on a call type, techs to offer first. */
function ChipPicker({ values, options, onChange, addLabel, disabled, labelOf }: {
  values: string[];
  /** What can be added. */
  options: { value: string; label: string }[];
  onChange: (next: string[]) => void;
  addLabel: string;
  disabled?: boolean;
  /** Names for values no longer addable (an archived specialty). */
  labelOf?: (v: string) => string;
}) {
  const nameOf = (v: string) => options.find((o) => o.value === v)?.label ?? labelOf?.(v) ?? v;
  const rest = options.filter((o) => !values.includes(o.value));
  return (
    <div className="flex flex-wrap items-center gap-1">
      {values.map((v) => (
        <span key={v} className="inline-flex items-center gap-0.5 rounded-full border border-hairline bg-white py-0.5 pl-2 pr-0.5 text-[12px] font-semibold text-slate-700">
          {nameOf(v)}
          <button type="button" disabled={disabled} onClick={() => onChange(values.filter((x) => x !== v))} aria-label={`Remove ${nameOf(v)}`}
            className="rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50">
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      {rest.length > 0 && (
        <select
          value=""
          disabled={disabled}
          onChange={(e) => { if (e.target.value) onChange([...values, e.target.value]); }}
          aria-label={addLabel.replace(/^\+\s*/, "Add ")}
          title={addLabel.replace(/^\+\s*/, "Add ")}
          style={{ width: values.length ? 26 : addLabel.length * 7 + 14 }}
          className="h-6 cursor-pointer appearance-none rounded-full border border-dashed border-slate-300 bg-transparent px-1 text-center text-[11.5px] font-semibold text-slate-500 hover:border-slate-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
        >
          <option value="">{values.length ? "+" : addLabel}</option>
          {rest.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
    </div>
  );
}

export function SkillsLevelsSection() {
  const { data: techs, error: techsErr } = useActiveTechs("03");
  const { data: quals, isLoading: qualsLoading, error: qualsErr } = useTechQuals();
  const { data: specialties, error: specErr } = useSpecialties();
  const { data: types, error: typesErr } = useServiceTypes();
  const { data: eqCats, error: eqErr } = useEquipmentCategories();
  const loadErr = techsErr ?? qualsErr ?? specErr ?? typesErr ?? eqErr;
  const labels = useLevelLabels();
  const setLevel = useSetTechLevel();
  const setSkill = useSetTechSkill();
  const saveSpec = useSaveSpecialty();
  const saveType = useSaveTypeNeeds();
  const saveEquip = useUpdateEquipmentCategory();
  const saveSetting = useSaveOrgSetting();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<Set<string>>(() => new Set());

  const roster = useMemo(
    () => (techs ?? []).slice().sort((a, b) => a.empno.localeCompare(b.empno)),
    [techs],
  );
  const first = (e: string) => {
    const t = roster.find((x) => x.empno === e);
    return (t?.firstname || t?.empname?.split(" ")[0] || e).trim();
  };
  const active = (specialties ?? []).filter((s) => s.active);
  const archived = (specialties ?? []).filter((s) => !s.active);
  const specOptions = active.map((s) => ({ value: String(s.id), label: s.name }));
  const techOptions = roster.map((t) => ({ value: t.empno, label: first(t.empno) }));

  /** Run a save with a per-control busy flag and one error line. */
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setErr(null);
    setBusy((b) => new Set(b).add(key));
    try { await fn(); } catch (e) { setErr(errText(e)); }
    finally { setBusy((b) => { const n = new Set(b); n.delete(key); return n; }); }
  };

  // Level names
  const [names, setNames] = useState<string[] | null>(null);
  const shownNames = names ?? labels;
  const namesDirty = !!names && names.some((n, i) => n.trim() !== labels[i]);

  // Specialty add / rename
  const [adding, setAdding] = useState("");
  const [renameId, setRenameId] = useState<number | null>(null);
  const [renameTo, setRenameTo] = useState("");

  const holders = (id: number) => roster.filter((t) => quals?.get(t.empno)?.skills.has(id));
  const unplaced = roster.filter((t) => quals && quals.get(t.empno)?.level == null);

  const typeGroups = useMemo(() => {
    const m = new Map<string, ServiceType[]>();
    for (const t of types ?? []) m.set(t.category, [...(m.get(t.category) ?? []), t]);
    return [...m.entries()];
  }, [types]);
  const equipRows = (eqCats ?? []).filter((c) => c.active && c.grouping === "system");

  // Wait for every list before drawing the grids — a half-loaded page shows
  // empty tables and "archived" placeholders that look like lost settings.
  if (!techs || !quals || !specialties || !types || !eqCats) {
    return loadErr ? (
      <p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-[13px] font-medium text-rose-700">
        Couldn't load skills — {errText(loadErr)}. Refresh the page to try again.
      </p>
    ) : (
      <div className="flex items-center gap-2 rounded-2xl border border-hairline bg-white px-4 py-6 text-[13px] text-slate-500 shadow-e1">
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> Loading techs, specialties and call types…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {err && <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] font-medium text-rose-700">Couldn't save — {err}</p>}

      {/* ── Techs: level + who can do what ── */}
      <SectionCard icon={Users} title="Techs"
        info="When you book, the grid offers the techs who fit, first choices first; anyone can still be picked by hand, and Pegasus flags it instead of blocking it. Changes here save as you make them.">
        {unplaced.length > 0 && (
          <p className="mb-2 text-[12px] text-amber-700">
            No level yet: {unplaced.map((t) => first(t.empno)).join(", ")} — not offered for calls that need a level until one is set.
          </p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="text-left">
                <th className="w-[190px] py-1.5 pr-2 text-[10.5px] font-bold uppercase tracking-wider text-slate-400">Specialty</th>
                {roster.map((t) => (
                  <th key={t.empno} className="px-1 py-1.5 text-center text-[12.5px] font-bold text-slate-800">{first(t.empno)}</th>
                ))}
                <th className="w-[110px] py-1.5 pl-2 text-[10.5px] font-bold uppercase tracking-wider text-slate-400">Covered</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-y border-slate-100 bg-slate-50/60">
                <td className="py-1.5 pr-2 font-semibold text-slate-700">Level</td>
                {roster.map((t) => {
                  const lv = quals?.get(t.empno)?.level ?? null;
                  const key = `lv:${t.empno}`;
                  return (
                    <td key={t.empno} className="px-1 py-1 text-center">
                      {/* The label wraps in a narrow column; the native select sits on top, invisible. */}
                      <label className={cn(
                        "relative mx-auto flex min-h-[34px] w-full max-w-[96px] cursor-pointer items-center justify-center rounded-lg border px-1 py-0.5 text-[11px] font-semibold leading-tight transition-colors focus-within:ring-2 focus-within:ring-brand-400/30",
                        lv ? "border-hairline bg-white text-slate-700 hover:bg-slate-50" : "border-amber-300 bg-amber-50/60 text-amber-700",
                        busy.has(key) && "opacity-60",
                      )}>
                        {lv ? labels[lv - 1] : "Set level"}
                        <select
                          value={lv ?? ""}
                          disabled={busy.has(key) || qualsLoading}
                          onChange={(e) => run(key, () => setLevel.mutateAsync({ empno: t.empno, level: e.target.value ? Number(e.target.value) : null }))}
                          aria-label={`${first(t.empno)}'s level`}
                          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        >
                          <option value="">No level</option>
                          {LEVELS.map((n) => <option key={n} value={n}>{labels[n - 1]}</option>)}
                        </select>
                      </label>
                    </td>
                  );
                })}
                <td />
              </tr>
              {active.map((s) => {
                const h = holders(s.id);
                return (
                  <tr key={s.id} className="group border-b border-slate-100 last:border-0">
                    <td className="py-1 pr-2">
                      {renameId === s.id ? (
                        <form
                          onSubmit={(e) => { e.preventDefault(); void run(`sp:${s.id}`, () => saveSpec.mutateAsync({ id: s.id, name: renameTo })).then(() => setRenameId(null)); }}
                          className="flex items-center gap-1"
                        >
                          <input autoFocus value={renameTo} onChange={(e) => setRenameTo(e.target.value)} className="w-full rounded-md border border-hairline px-1.5 py-0.5 text-[12.5px]" />
                          <button type="submit" className="rounded-md bg-brand-700 p-1 text-white" aria-label="Save name"><Check className="h-3 w-3" /></button>
                          <button type="button" onClick={() => setRenameId(null)} className="p-1 text-slate-400 hover:text-slate-600" aria-label="Cancel"><X className="h-3 w-3" /></button>
                        </form>
                      ) : (
                        <span className="flex items-center gap-1">
                          <span className="font-semibold text-slate-800" title={[s.description, s.qualifier].filter(Boolean).join(" · ") || undefined}>{s.name}</span>
                          <button type="button" onClick={() => { setRenameId(s.id); setRenameTo(s.name); }} aria-label={`Rename ${s.name}`}
                            className="rounded p-0.5 text-slate-300 opacity-0 transition-opacity hover:text-slate-600 group-hover:opacity-100 focus:opacity-100">
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button type="button" onClick={() => run(`sp:${s.id}`, () => saveSpec.mutateAsync({ id: s.id, active: false }))}
                            title="Archive — it stops being a need anywhere; the techs keep it on file"
                            className="rounded px-1 text-[10.5px] font-semibold text-slate-300 opacity-0 transition-opacity hover:text-slate-600 group-hover:opacity-100 focus:opacity-100">
                            Archive
                          </button>
                        </span>
                      )}
                    </td>
                    {roster.map((t) => {
                      const on = !!quals?.get(t.empno)?.skills.has(s.id);
                      const key = `sk:${t.empno}:${s.id}`;
                      return (
                        <td key={t.empno} className="px-1 py-1 text-center">
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={busy.has(key) || qualsLoading}
                            onChange={(e) => run(key, () => setSkill.mutateAsync({ empno: t.empno, skillId: s.id, on: e.target.checked }))}
                            aria-label={`${first(t.empno)} — ${s.name}`}
                            className="h-4 w-4 cursor-pointer accent-brand-600 disabled:cursor-wait"
                          />
                        </td>
                      );
                    })}
                    <td className={cn("py-1 pl-2 text-[11.5px]", h.length === 0 ? "font-semibold text-rose-600" : h.length === 1 ? "font-semibold text-amber-700" : "text-slate-500")}>
                      {h.length === 0 ? "Nobody" : h.length === 1 ? `Only ${first(h[0].empno)}` : `${h.length} techs`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); void run("sp:new", () => saveSpec.mutateAsync({ name: adding, sort: ((specialties ?? []).reduce((m, x) => Math.max(m, x.sort), 0) || 0) + 10 })).then(() => setAdding("")); }}
          className="mt-2.5 flex flex-wrap items-center gap-2"
        >
          <input value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="New specialty — e.g. Geothermal"
            className="h-8 min-w-[220px] rounded-md border border-hairline px-2 text-[12.5px] focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30" />
          <button type="submit" disabled={!adding.trim() || busy.has("sp:new")}
            className="inline-flex h-8 items-center gap-1 rounded-md bg-brand-700 px-2.5 text-[12px] font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300">
            <Plus className="h-3.5 w-3.5" /> Add Specialty
          </button>
          {archived.length > 0 && (
            <span className="ml-auto flex flex-wrap items-center gap-1.5 text-[11.5px] text-slate-400">
              Archived:
              {archived.map((s) => (
                <button key={s.id} type="button" onClick={() => run(`sp:${s.id}`, () => saveSpec.mutateAsync({ id: s.id, active: true }))}
                  title="Bring it back" className="inline-flex items-center gap-1 rounded-full border border-hairline px-2 py-0.5 font-semibold text-slate-500 hover:bg-slate-50">
                  {s.name} <RotateCcw className="h-3 w-3" />
                </button>
              ))}
            </span>
          )}
        </form>
      </SectionCard>

      {/* ── Level names ── */}
      <SectionCard
        icon={GraduationCap}
        title="Level Names"
        info="Lowest to highest. Rename them to what the shop says, like Journeyman."
        right={namesDirty ? (
          <button type="button" disabled={busy.has("names")}
            onClick={() => run("names", () => saveSetting.mutateAsync({ key: "tech_levels", value: { labels: shownNames.map((n, i) => n.trim() || labels[i]) } })).then(() => setNames(null))}
            className="rounded-md bg-brand-700 px-2.5 py-1 text-[12px] font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300">
            Save Names
          </button>
        ) : null}
      >
        <div className="flex flex-wrap items-center gap-2">
          {LEVELS.map((n, i) => (
            <label key={n} className="flex items-center gap-1.5 text-[12px] text-slate-500">
              <span className="font-bold text-slate-400">{n}</span>
              <input
                value={shownNames[i] ?? ""}
                onChange={(e) => setNames(shownNames.map((x, j) => (j === i ? e.target.value : x)))}
                className="h-8 w-[170px] rounded-md border border-hairline px-2 text-[12.5px] font-semibold text-slate-800 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
              {n < LEVELS.length && <span className="text-slate-300">→</span>}
            </label>
          ))}
        </div>
      </SectionCard>

      {/* ── Call types ── */}
      <SectionCard icon={Tag} title="Call Types">
        <table className="w-full border-collapse text-[12.5px]">
          <thead>
            <tr className="text-left text-[10.5px] font-bold uppercase tracking-wider text-slate-400">
              <th className="py-1.5 pr-2">Call Type</th>
              <th className="w-[200px] py-1.5 pr-2">Lowest Level</th>
              <th className="py-1.5 pr-2">Needs</th>
              <th className="py-1.5">Offer First</th>
            </tr>
          </thead>
          <tbody>
            {typeGroups.map(([cat, list]) => (
              <FragmentRows key={cat} label={categoryLabel(cat)}>
                {list.map((t) => {
                  const key = `ty:${t.id}`;
                  return (
                    <tr key={t.id} className="border-b border-slate-100 align-middle last:border-0">
                      <td className="py-1.5 pr-2 font-semibold text-slate-800">{t.name}</td>
                      <td className="py-1.5 pr-2">
                        <select
                          value={t.min_level ?? ""}
                          disabled={busy.has(key)}
                          onChange={(e) => run(key, () => saveType.mutateAsync({ id: t.id, min_level: e.target.value ? Number(e.target.value) : null }))}
                          className="w-full rounded-md border border-hairline bg-white px-1.5 py-1 text-[12px] font-semibold text-slate-700"
                        >
                          <option value="">Anyone</option>
                          {LEVELS.map((n) => <option key={n} value={n}>{levelNeedText(labels, n)}</option>)}
                        </select>
                      </td>
                      <td className="py-1.5 pr-2">
                        <ChipPicker
                          values={(t.needs_skills ?? []).map(String)}
                          options={specOptions}
                          labelOf={(v) => `${(specialties ?? []).find((x) => String(x.id) === v)?.name ?? "Unknown"} (archived)`}
                          disabled={busy.has(key)}
                          addLabel="+ Specialty"
                          onChange={(next) => run(key, () => saveType.mutateAsync({ id: t.id, needs_skills: next.map(Number) }))}
                        />
                      </td>
                      <td className="py-1.5">
                        <ChipPicker
                          values={t.offer_first ?? []}
                          options={techOptions}
                          disabled={busy.has(key)}
                          addLabel="+ Tech"
                          onChange={(next) => run(key, () => saveType.mutateAsync({ id: t.id, offer_first: next }))}
                        />
                      </td>
                    </tr>
                  );
                })}
              </FragmentRows>
            ))}
          </tbody>
        </table>
      </SectionCard>

      {/* ── Equipment ── */}
      <SectionCard icon={Boxes} title="Equipment"
        info="When the only unit on file for the call is one of these, its specialty applies on its own; when the home has a mix, the booking asks.">
        <table className="w-full border-collapse text-[12.5px]">
          <thead>
            <tr className="text-left text-[10.5px] font-bold uppercase tracking-wider text-slate-400">
              <th className="py-1.5 pr-2">Equipment</th>
              <th className="w-[220px] py-1.5 pr-2">Needs</th>
              <th className="py-1.5">Offer First</th>
            </tr>
          </thead>
          <tbody>
            {equipRows.map((c) => {
              const key = `eq:${c.key}`;
              return (
                <tr key={c.key} className="border-b border-slate-100 align-middle last:border-0">
                  <td className="py-1.5 pr-2 font-semibold text-slate-800">{c.label}</td>
                  <td className="py-1.5 pr-2">
                    <select
                      value={c.needs_skill_id ?? ""}
                      disabled={busy.has(key)}
                      onChange={(e) => run(key, () => saveEquip.mutateAsync({ key: c.key, needs_skill_id: e.target.value ? Number(e.target.value) : null }))}
                      className={cn("w-full rounded-md border border-hairline bg-white px-1.5 py-1 text-[12px] font-semibold", c.needs_skill_id ? "text-slate-700" : "text-slate-400")}
                    >
                      <option value="">No specialty</option>
                      {active.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      {c.needs_skill_id != null && !active.some((s) => s.id === c.needs_skill_id) && (
                        <option value={c.needs_skill_id}>{(specialties ?? []).find((s) => s.id === c.needs_skill_id)?.name ?? "Archived"} (archived)</option>
                      )}
                    </select>
                  </td>
                  <td className="py-1.5">
                    <ChipPicker
                      values={c.offer_first ?? []}
                      options={techOptions}
                      disabled={busy.has(key)}
                      addLabel="+ Tech"
                      onChange={(next) => run(key, () => saveEquip.mutateAsync({ key: c.key, offer_first: next }))}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </SectionCard>
    </div>
  );
}

/** A category label row followed by its call types. */
function FragmentRows({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <tr>
        <td colSpan={4} className="pb-0.5 pt-3 text-[10.5px] font-bold uppercase tracking-wider text-slate-400">{label}</td>
      </tr>
      {children}
    </>
  );
}
